// Hook to fetch and subscribe to the singleton site_settings row.
// Used by the announcement banner and maintenance gate. Cached in React Query.
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SiteSettings = {
  banner_enabled: boolean;
  banner_message: string;
  banner_link_url: string;
  banner_link_label: string;
  banner_variant: "info" | "success" | "warning" | "promo";
  maintenance_enabled: boolean;
  maintenance_message: string;
  feature_flags: Record<string, boolean>;
  updated_at: string;
};

const DEFAULTS: SiteSettings = {
  banner_enabled: false,
  banner_message: "",
  banner_link_url: "",
  banner_link_label: "",
  banner_variant: "info",
  maintenance_enabled: false,
  maintenance_message: "",
  feature_flags: {},
  updated_at: new Date(0).toISOString(),
};

export const useSiteSettings = () => {
  const qc = useQueryClient();

  const query = useQuery<SiteSettings>({
    queryKey: ["site_settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select(
          "banner_enabled, banner_message, banner_link_url, banner_link_label, banner_variant, maintenance_enabled, maintenance_message, feature_flags, updated_at",
        )
        .limit(1)
        .maybeSingle();
      if (error) {
        console.warn("[useSiteSettings] fetch failed", error);
        return DEFAULTS;
      }
      if (!data) return DEFAULTS;
      return {
        ...DEFAULTS,
        ...data,
        feature_flags: (data.feature_flags ?? {}) as Record<string, boolean>,
      } as SiteSettings;
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  // Live updates via realtime so admin changes appear without a refresh.
  // Use a unique channel name per hook instance to avoid the
  // "cannot add postgres_changes callbacks after subscribe()" error that
  // occurs when multiple components mount this hook and reuse the same
  // channel name.
  useEffect(() => {
    const channelName = `site_settings_live:${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "site_settings" },
        () => qc.invalidateQueries({ queryKey: ["site_settings"] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);

  return {
    settings: query.data ?? DEFAULTS,
    loading: query.isLoading,
    refetch: query.refetch,
  };
};
